const _excluded = ["onChange", "value"],
  _excluded2 = ["onChange"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Fragment, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { range } from 'lodash-es';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useOverlayContext, Button, Card, InfoIcon, MenuItem, Page, Select, Typography, Checkbox, WithInfoIcon } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { accountantAccessInfoTooltip } from "../../business-logic/accountantAccessLogic";
import { getAllAutoApprovalDays } from "../../business-logic/autoApprovalLogic";
import IntroVideoField from "../../components/AgreementStudioPage/AgreementStudioModal/IntroVideo/IntroVideoField";
import { LegalTermsDropdown } from "../../components/LegalTermsDropdown/LegalTermsDropdown";
import { NavigationGuardModal } from "../../components/NavigationGuardModal";
import { businessSettingsDefaults, daysOfWeek, netTermsOptions } from "../../constants";
import { useStoreActions } from "../../contexts";
import { useLegalTerms } from "../../hooks/reactQuery/queries/useLegalTerms";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { useLocaleKeys } from "../../i18n";
import { sendErrReport } from "../../infra/utils";
import { BillingDayOfMonthModel, BillingDayOfMonthType, approvalMethodNoticePeriodModel } from "../../models";
import { makeExpirationDate } from "../../models/expiration-date";
import { RequestAccountantAccess, requestAccountantAccessModel } from "../../models/request-accountant-access";
import { selectors } from "../../store/state-selectors";
import { useStore } from "../../store/store-context";
import { StartBillingOnEnum } from "../../types/api.model";
import pluralize from "../../utils/pluralize";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import IntroductoryMessageEditor from "./IntroductoryMessageEditor/IntroductoryMessageEditor";
import styles from "./ProposalDefaults.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const testPrefix = 'settings-business-settings';
const updateLoader = 'updateBusinessSettings';
var FormFieldType = /*#__PURE__*/function (FormFieldType) {
  FormFieldType["Input"] = "input";
  FormFieldType["Heading"] = "heading";
  FormFieldType["Separator"] = "separator";
  FormFieldType["LegalTermsUpload"] = "legalTermsUpload";
  return FormFieldType;
}(FormFieldType || {});
const formId = 'business-settings-form';
export const {
  ProposalDefaultsTID
} = getTestIds();
export default function ProposalDefaults() {
  var _worksAt$settings, _worksAt$settings2, _worksAt$settings3, _worksAt$settings4, _worksAt$settings5, _worksAt$settings6, _worksAt$settings7, _worksAt$settings8, _worksAt$settings9, _worksAt$settings10, _worksAt$settings11, _worksAt$settings12, _worksAt$settings13, _worksAt$settings14, _worksAt$settings15, _worksAt$settings16, _worksAt$settings17, _worksAt$settings18, _worksAt$settings19, _worksAt$settings20;
  const worksAt = useStore(selectors.user.business);
  const {
    api,
    setWorksAt,
    fetchUserInfo
  } = useStoreActions();
  const {
    startLoader,
    stopLoader,
    showErrorToast
  } = useOverlayContext();
  const {
    legalTerms
  } = useLegalTerms({
    refreshOnHookMount: true,
    withLoader: true
  });
  const [isLegalTermsFieldDirty, setIsLegalTermsFieldDirty] = useState(false);
  const {
    t
  } = useLocaleKeys('pages.proposalDefaults');
  const billingDayOfMonthOptions = range(1, 29).map(day => BillingDayOfMonthModel.make(day)).concat([BillingDayOfMonthModel.make()]);
  const autoApprovalOptions = useMemo(() => {
    return [approvalMethodNoticePeriodModel.makeManualApprovalMethodNoticePeriod()].concat(getAllAutoApprovalDays().map(approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod));
  }, []);
  const startBillingOnMode = (worksAt === null || worksAt === void 0 || (_worksAt$settings = worksAt.settings) === null || _worksAt$settings === void 0 ? void 0 : _worksAt$settings.startBillingOn) || businessSettingsDefaults.startBillingOn || StartBillingOnEnum.SkipBillingOnAcceptance;
  const selectedBillingDayOfMonth = (worksAt === null || worksAt === void 0 || (_worksAt$settings2 = worksAt.settings) === null || _worksAt$settings2 === void 0 ? void 0 : _worksAt$settings2.billingDayOfMonth) || businessSettingsDefaults.billingDayOfMonth;
  if (selectedBillingDayOfMonth.type === BillingDayOfMonthType.LastDayOfMonth) delete selectedBillingDayOfMonth['day'];
  const defaultValues = {
    legalTermsDocumentId: (worksAt === null || worksAt === void 0 || (_worksAt$settings3 = worksAt.settings) === null || _worksAt$settings3 === void 0 ? void 0 : _worksAt$settings3.legalTermsDocumentId) || businessSettingsDefaults.legalTermsDocumentId,
    disablePlaidLink: (worksAt === null || worksAt === void 0 || (_worksAt$settings4 = worksAt.settings) === null || _worksAt$settings4 === void 0 ? void 0 : _worksAt$settings4.disablePlaidLink) || businessSettingsDefaults.disablePlaidLink,
    netTerms: (worksAt === null || worksAt === void 0 || (_worksAt$settings5 = worksAt.settings) === null || _worksAt$settings5 === void 0 ? void 0 : _worksAt$settings5.netTerms) || businessSettingsDefaults.netTerms,
    billingDayOfMonth: selectedBillingDayOfMonth,
    billingDayOfWeek: (worksAt === null || worksAt === void 0 || (_worksAt$settings6 = worksAt.settings) === null || _worksAt$settings6 === void 0 ? void 0 : _worksAt$settings6.billingDayOfWeek) || businessSettingsDefaults.billingDayOfWeek,
    feeControls: {
      coverCreditCardFees: (worksAt === null || worksAt === void 0 || (_worksAt$settings7 = worksAt.settings) === null || _worksAt$settings7 === void 0 ? void 0 : _worksAt$settings7.feeControls.coverCreditCardFees) || businessSettingsDefaults.feeControls.coverCreditCardFees,
      coverDirectDebitFees: (worksAt === null || worksAt === void 0 || (_worksAt$settings8 = worksAt.settings) === null || _worksAt$settings8 === void 0 ? void 0 : _worksAt$settings8.feeControls.coverDirectDebitFees) || businessSettingsDefaults.feeControls.coverDirectDebitFees
    },
    amendmentsApprovalMethod: (worksAt === null || worksAt === void 0 || (_worksAt$settings9 = worksAt.settings) === null || _worksAt$settings9 === void 0 ? void 0 : _worksAt$settings9.amendmentsApprovalMethod) || businessSettingsDefaults.amendmentsApprovalMethod,
    invoicesApprovalMethod: (worksAt === null || worksAt === void 0 || (_worksAt$settings10 = worksAt.settings) === null || _worksAt$settings10 === void 0 ? void 0 : _worksAt$settings10.invoicesApprovalMethod) || businessSettingsDefaults.invoicesApprovalMethod,
    billUpfront: (worksAt === null || worksAt === void 0 || (_worksAt$settings11 = worksAt.settings) === null || _worksAt$settings11 === void 0 ? void 0 : _worksAt$settings11.billUpfront) || businessSettingsDefaults.billUpfront,
    automaticServicesPausedByDefault: (worksAt === null || worksAt === void 0 || (_worksAt$settings12 = worksAt.settings) === null || _worksAt$settings12 === void 0 ? void 0 : _worksAt$settings12.automaticServicesPausedByDefault) || businessSettingsDefaults.automaticServicesPausedByDefault,
    prorateFirstInvoice: (worksAt === null || worksAt === void 0 || (_worksAt$settings13 = worksAt.settings) === null || _worksAt$settings13 === void 0 ? void 0 : _worksAt$settings13.prorateFirstInvoice) || businessSettingsDefaults.prorateFirstInvoice,
    requestAccountantAccess: (worksAt === null || worksAt === void 0 || (_worksAt$settings14 = worksAt.settings) === null || _worksAt$settings14 === void 0 ? void 0 : _worksAt$settings14.requestAccountantAccess) || businessSettingsDefaults.requestAccountantAccess,
    introductoryMessages: {
      proposal: (worksAt === null || worksAt === void 0 || (_worksAt$settings15 = worksAt.settings) === null || _worksAt$settings15 === void 0 ? void 0 : _worksAt$settings15.introductoryMessages.proposal) || businessSettingsDefaults.introductoryMessages.proposal,
      invitation: (worksAt === null || worksAt === void 0 || (_worksAt$settings16 = worksAt.settings) === null || _worksAt$settings16 === void 0 ? void 0 : _worksAt$settings16.introductoryMessages.invitation) || businessSettingsDefaults.introductoryMessages.invitation
    },
    startBillingOn: (worksAt === null || worksAt === void 0 || (_worksAt$settings17 = worksAt.settings) === null || _worksAt$settings17 === void 0 ? void 0 : _worksAt$settings17.startBillingOn) || businessSettingsDefaults.startBillingOn,
    introVideo: (worksAt === null || worksAt === void 0 || (_worksAt$settings18 = worksAt.settings) === null || _worksAt$settings18 === void 0 ? void 0 : _worksAt$settings18.introVideo) || businessSettingsDefaults.introVideo,
    selectedTemplate: 'introductoryMessages.proposal',
    startPaused: startBillingOnMode === StartBillingOnEnum.StartPaused,
    billOnAcceptance: startBillingOnMode === StartBillingOnEnum.Earliest,
    expirationDate: (worksAt === null || worksAt === void 0 || (_worksAt$settings19 = worksAt.settings) === null || _worksAt$settings19 === void 0 ? void 0 : _worksAt$settings19.expirationDate) || businessSettingsDefaults.expirationDate,
    invoicePrefix: (worksAt === null || worksAt === void 0 || (_worksAt$settings20 = worksAt.settings) === null || _worksAt$settings20 === void 0 ? void 0 : _worksAt$settings20.invoicePrefix) || businessSettingsDefaults.invoicePrefix
  };
  delete defaultValues['defaultLegalTermsDocumentId'];
  delete defaultValues['defaultBillingDayOfMonth'];
  const {
    control,
    handleSubmit,
    formState,
    getValues,
    reset,
    setValue
  } = useForm({
    defaultValues,
    mode: 'onSubmit'
  });
  const [lastDayOptionDisabled, setLastDayOptionDisabled] = useState(getValues('billUpfront'));
  const billUpfront = useWatch({
    name: 'billUpfront',
    control
  });
  const startPaused = useWatch({
    name: 'startPaused',
    control
  });
  const billOnAcceptance = useWatch({
    name: 'billOnAcceptance',
    control
  });
  const prorateFirstInvoice = useWatch({
    name: 'prorateFirstInvoice',
    control
  });
  const startBillingOn = useWatch({
    name: 'startBillingOn',
    control
  });
  const introVideo = useWatch({
    name: 'introVideo',
    control
  });
  const billingDayOfMonth = useWatch({
    name: 'billingDayOfMonth.type',
    control
  });
  const legalTermsDocumentId = useWatch({
    name: 'legalTermsDocumentId',
    control
  });
  const [isProrationDisabled, setIsProrationDisabled] = useState(false);
  const [isStartPausedDisabled, setIsStartPausedDisabled] = useState(false);
  const [isBillOnAcceptanceDisabled, setIsBillOnAcceptanceDisabled] = useState(false);
  const billUpfrontErrorShown = billUpfront && billingDayOfMonth === BillingDayOfMonthType.LastDayOfMonth;
  useSetDocumentTitle(t.pageTitle());
  useEffect(() => {
    if (!billUpfront && startBillingOn === StartBillingOnEnum.Earliest) {
      setValue('startBillingOn', StartBillingOnEnum.SkipBillingOnAcceptance);
    }
  }, [billUpfront, startBillingOn]);
  useEffect(() => {
    const isProrationInvalid = startPaused || !billOnAcceptance && billUpfront;
    setIsProrationDisabled(isProrationInvalid);
    setValue('prorateFirstInvoice', isProrationInvalid ? false : prorateFirstInvoice);
  }, [startPaused, billOnAcceptance, billUpfront, setValue, prorateFirstInvoice]);
  useEffect(() => {
    const isStartPausedInvalid = billUpfront && billOnAcceptance;
    setIsStartPausedDisabled(isStartPausedInvalid);
    setValue('startPaused', isStartPausedInvalid ? false : startPaused);
  }, [billOnAcceptance, billUpfront, setValue, startPaused]);
  useEffect(() => {
    const isBillOnAcceptanceInvalid = startPaused || !billUpfront;
    setIsBillOnAcceptanceDisabled(isBillOnAcceptanceInvalid);
    setValue('billOnAcceptance', isBillOnAcceptanceInvalid ? false : billOnAcceptance);
  }, [startPaused, billUpfront, setValue, billOnAcceptance]);
  const selectedLegalTerms = useMemo(() => {
    return legalTerms.find(lt => lt.id === legalTermsDocumentId);
  }, [legalTermsDocumentId, legalTerms]);
  const fields = [{
    type: FormFieldType.Heading,
    label: t.sections.generalSettings()
  }, {
    type: FormFieldType.LegalTermsUpload,
    label: t.fields.termsOfService()
  }, {
    type: FormFieldType.Input,
    name: 'amendmentsApprovalMethod',
    label: t.fields.amendmentsApproval.label(),
    tooltip: t.fields.amendmentsApproval.tooltip(),
    options: autoApprovalOptions.map(option => ({
      value: option,
      label: approvalMethodNoticePeriodModel.isAutoApproval(option) ? t.options.enabledWithNoticeDays({
        days: pluralize('day', option.auto.noticePeriodDays, true)
      }) : t.options.disabled()
    }))
  }, {
    type: FormFieldType.Input,
    name: 'invoicesApprovalMethod',
    label: t.fields.invoicesApproval.label(),
    tooltip: t.fields.invoicesApproval.tooltip(),
    options: autoApprovalOptions.map(option => ({
      value: option,
      label: approvalMethodNoticePeriodModel.isAutoApproval(option) ? t.options.enabledWithNoticeDays({
        days: pluralize('day', option.auto.noticePeriodDays, true)
      }) : t.options.disabled()
    }))
  }, {
    type: FormFieldType.Input,
    name: 'netTerms',
    label: t.fields.netTerms.label(),
    options: netTermsOptions.map(numOfDays => ({
      label: numOfDays === 0 ? t.fields.netTerms.onAcceptance() : pluralize('day', numOfDays, true),
      value: numOfDays
    }))
  }, {
    type: FormFieldType.Input,
    name: 'feeControls.coverCreditCardFees',
    label: t.fields.creditCardFees.label(),
    options: [{
      label: t.fields.creditCardFees.theClient(),
      value: false
    }, {
      label: (worksAt === null || worksAt === void 0 ? void 0 : worksAt.name) || t.fields.creditCardFees.theVendor(),
      value: true
    }]
  }, {
    type: FormFieldType.Input,
    name: 'expirationDate',
    label: t.fields.expirationDate.label(),
    options: [{
      label: t.fields.expirationDate.noExpiration(),
      value: makeExpirationDate()
    }, ...range(1, 91).map(day => ({
      label: day + '',
      value: makeExpirationDate(day)
    }))]
  }, {
    type: FormFieldType.Input,
    name: 'requestAccountantAccess',
    label: t.fields.accountantAccess.label(),
    tooltip: accountantAccessInfoTooltip,
    isCheckbox: true,
    mapValue: value => requestAccountantAccessModel.wasRequested(value),
    mapOnChange: event => event.target.checked ? RequestAccountantAccess.Mandatory : RequestAccountantAccess.None
  }, {
    type: FormFieldType.Separator
  }, {
    type: FormFieldType.Heading,
    label: t.sections.automaticServicesSettings()
  }, {
    type: FormFieldType.Input,
    name: 'billUpfront',
    label: t.fields.billUpfront.label(),
    options: [{
      value: true,
      label: t.fields.billUpfront.upfront()
    }, {
      value: false,
      label: t.fields.billUpfront.endOfCycle()
    }]
  }, {
    type: FormFieldType.Input,
    name: 'billingDayOfMonth',
    label: t.fields.billingDayOfMonth.label(),
    tooltip: t.fields.billingDayOfMonth.tooltip(),
    options: billingDayOfMonthOptions.map(option => ({
      label: option.formatDay(t.fields.billingDayOfMonth.lastDay()),
      value: option,
      disabled: option.isLast() && lastDayOptionDisabled
    })),
    error: billUpfrontErrorShown,
    helperText: billUpfrontErrorShown ? t.fields.billingDayOfMonth.error() : ''
  }, {
    type: FormFieldType.Input,
    name: 'billingDayOfWeek',
    label: t.fields.billingDayOfWeek.label(),
    tooltip: t.fields.billingDayOfWeek.tooltip(),
    options: daysOfWeek.map(value => ({
      value,
      label: value
    }))
  }, {
    type: FormFieldType.Input,
    isCheckbox: true,
    name: 'startPaused',
    disabled: isStartPausedDisabled,
    label: t.fields.startPaused.label(),
    tooltip: t.fields.startPaused.tooltip(),
    options: [{
      label: t.options.disabled(),
      value: false
    }, {
      label: t.options.enabled(),
      value: true
    }]
  }, {
    type: FormFieldType.Input,
    isCheckbox: true,
    name: 'billOnAcceptance',
    disabled: isBillOnAcceptanceDisabled,
    label: t.fields.billOnAcceptance.label(),
    tooltip: t.fields.billOnAcceptance.tooltip(),
    options: [{
      label: t.options.disabled(),
      value: false
    }, {
      label: t.options.enabled(),
      value: true
    }]
  }, {
    type: FormFieldType.Input,
    isCheckbox: true,
    name: 'prorateFirstInvoice',
    label: t.fields.prorateFirstInvoice.label(),
    disabled: isProrationDisabled,
    tooltip: t.fields.prorateFirstInvoice.tooltip(),
    options: [{
      label: t.options.disabled(),
      value: false
    }, {
      label: t.options.enabled(),
      value: true
    }]
  }, {
    type: FormFieldType.Separator
  }];
  const onSubmit = async values => {
    if (!worksAt) return;
    if (billUpfrontErrorShown) return;
    startLoader(updateLoader);
    const params = _objectSpread(_objectSpread({}, values), {}, {
      coverCreditCardFees: values.feeControls.coverCreditCardFees,
      startBillingOn: values.startPaused ? StartBillingOnEnum.StartPaused : values.billOnAcceptance ? StartBillingOnEnum.Earliest : StartBillingOnEnum.SkipBillingOnAcceptance
    });
    delete params['selectedTemplate'];
    delete params['feeControls'];
    delete params['disablePlaidLink'];
    delete params['startPaused'];
    delete params['billOnAcceptance'];
    try {
      await api.vendor.updateBusinessSettings(worksAt.id, params);
      const settings = await api.vendor.getBusinessSettings(worksAt.id);
      if (settings) {
        setWorksAt(Object.assign({}, worksAt, {
          settings
        }));
      }
      setIsLegalTermsFieldDirty(false);
      reset(undefined, {
        keepValues: true
      });
    } catch (error) {
      showErrorToast(t.errors.failedToSave(), 'save-error-toast');
      sendErrReport(error);
    }
    stopLoader(updateLoader);
  };
  const onDeleteLegalTerm = async legalTermsId => {
    const userInfo = await fetchUserInfo();
    if (legalTermsId === legalTermsDocumentId) {
      var _userInfo$business$se, _userInfo$business;
      const newLegalTermsDocumentId = (_userInfo$business$se = userInfo === null || userInfo === void 0 || (_userInfo$business = userInfo.business()) === null || _userInfo$business === void 0 || (_userInfo$business = _userInfo$business.settings) === null || _userInfo$business === void 0 ? void 0 : _userInfo$business.legalTermsDocumentId) !== null && _userInfo$business$se !== void 0 ? _userInfo$business$se : businessSettingsDefaults.legalTermsDocumentId;
      setValue('legalTermsDocumentId', newLegalTermsDocumentId, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  };
  return /*#__PURE__*/_jsxs(Page, {
    header: /*#__PURE__*/_jsx(Typography, {
      variant: "h5",
      children: t.pageHeader()
    }),
    subheader: /*#__PURE__*/_jsx(Typography, {
      children: t.pageSubheader()
    }),
    "data-testid": testPrefix + '-screen',
    minWidth: "978px",
    actions: /*#__PURE__*/_jsx(Button, {
      loading: formState.isSubmitting,
      "data-testid": testPrefix + '-btn-save-changes',
      form: formId,
      type: "submit",
      children: t.saveChanges()
    }),
    skin: "secondary",
    children: [/*#__PURE__*/_jsxs(Card, {
      autoHeight: true,
      children: [/*#__PURE__*/_jsx("div", {
        className: styles.titles
      }), /*#__PURE__*/_jsxs("form", {
        id: formId,
        className: styles.form,
        onSubmit: handleSubmit(onSubmit),
        children: [fields.map((fieldConfig, idx) => {
          if (fieldConfig.type === FormFieldType.Input) {
            const {
              name,
              label,
              tooltip,
              options,
              error,
              helperText,
              isCheckbox,
              disabled,
              mapValue,
              mapOnChange
            } = fieldConfig;
            if (isCheckbox) {
              return /*#__PURE__*/_jsx("div", {
                className: cn(styles.fieldWithTooltip, styles.checkboxField),
                children: /*#__PURE__*/_jsx(Controller, {
                  control: control,
                  name: name,
                  render: _ref => {
                    let {
                        field: {
                          onChange: _onChange,
                          value
                        }
                      } = _ref,
                      field = _objectWithoutProperties(_ref.field, _excluded);
                    return tooltip ? /*#__PURE__*/_jsx(WithInfoIcon, {
                      info: tooltip,
                      children: /*#__PURE__*/_jsx(Checkbox, _objectSpread(_objectSpread({
                        "data-testid": "".concat(testPrefix, "-").concat(name),
                        checked: mapValue ? mapValue(value) : value,
                        disabled: disabled,
                        onChange: event => _onChange(mapOnChange ? mapOnChange(event) : event)
                      }, field), {}, {
                        children: /*#__PURE__*/_jsx(Typography, {
                          skin: disabled ? 'disabled' : 'standard',
                          children: label
                        })
                      }), name)
                    }) : /*#__PURE__*/_jsx(Checkbox, _objectSpread(_objectSpread({
                      "data-testid": "".concat(testPrefix, "-").concat(name),
                      checked: value,
                      disabled: disabled,
                      onChange: _onChange
                    }, field), {}, {
                      children: /*#__PURE__*/_jsx(Typography, {
                        skin: disabled ? 'disabled' : 'standard',
                        children: label
                      })
                    }), name);
                  }
                })
              }, name);
            } else {
              return /*#__PURE__*/_jsxs(Fragment, {
                children: [/*#__PURE__*/_jsxs("div", {
                  className: styles.fieldWithTooltip,
                  children: [/*#__PURE__*/_jsx(Typography, {
                    children: label
                  }), tooltip && /*#__PURE__*/_jsx(InfoIcon, {
                    info: tooltip
                  })]
                }), /*#__PURE__*/_jsx(Controller, {
                  control: control,
                  name: name,
                  render: props => {
                    const _connectControllerToT = connectControllerToTextField(props),
                      {
                        onChange: _onChange2
                      } = _connectControllerToT,
                      rest = _objectWithoutProperties(_connectControllerToT, _excluded2);
                    const value = getValues(name);
                    const isObject = typeof value === 'object';
                    const primitiveValue = isObject ? JSON.stringify(value) : value;
                    return /*#__PURE__*/_jsx(Select, _objectSpread(_objectSpread({
                      collapseHelperText: false
                    }, rest), {}, {
                      value: primitiveValue,
                      onChange: e => {
                        if (isObject) {
                          e.target.value = JSON.parse(e.target.value);
                        }
                        _onChange2(e);
                      },
                      menuMaxHeight: 300,
                      SelectProps: {
                        onOpen: () => {
                          setLastDayOptionDisabled(getValues('billUpfront'));
                        }
                      },
                      error: error,
                      helperText: helperText,
                      "data-testid": "".concat(testPrefix, "-").concat(name),
                      children: options === null || options === void 0 ? void 0 : options.map((_ref2, i) => {
                        let {
                          value,
                          label,
                          disabled
                        } = _ref2;
                        const primitiveValue = typeof value === 'object' ? JSON.stringify(value) : value;
                        return /*#__PURE__*/_jsx(MenuItem, {
                          value: primitiveValue,
                          "data-testid": "".concat(testPrefix, "-").concat(name, "-option-").concat(i),
                          disabled: disabled,
                          children: label
                        }, primitiveValue);
                      })
                    }));
                  }
                })]
              }, name);
            }
          } else if (fieldConfig.type === FormFieldType.Heading) {
            return /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx(Typography, {
                variant: "paragraph1",
                weight: "normal",
                children: fieldConfig.label
              }), /*#__PURE__*/_jsx("div", {})]
            }, idx);
          } else if (fieldConfig.type === FormFieldType.Separator) {
            return /*#__PURE__*/_jsx(Fragment, {
              children: /*#__PURE__*/_jsx("div", {
                className: styles.separator
              })
            }, idx);
          } else if (fieldConfig.type === FormFieldType.LegalTermsUpload) {
            return /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx(Typography, {
                children: fieldConfig.label
              }), /*#__PURE__*/_jsx(Controller, {
                control: control,
                name: "legalTermsDocumentId",
                rules: {
                  required: t.errors.pleaseChooseDocument()
                },
                render: _ref3 => {
                  var _formState$errors$leg;
                  let {
                    field,
                    formState
                  } = _ref3;
                  return /*#__PURE__*/_jsx(LegalTermsDropdown, {
                    inputRef: field.ref,
                    error: formState.isSubmitted && !formState.isSubmitSuccessful ? (_formState$errors$leg = formState.errors.legalTermsDocumentId) === null || _formState$errors$leg === void 0 ? void 0 : _formState$errors$leg.message : undefined,
                    onDeleteLegalTerm: onDeleteLegalTerm,
                    viewMode: "advanced",
                    testid: ProposalDefaultsTID.LegalTermsDropdown(),
                    selectedLegalTermId: selectedLegalTerms === null || selectedLegalTerms === void 0 ? void 0 : selectedLegalTerms.id,
                    onSelectLegalTerm: legalTerm => {
                      var _legalTerm$id;
                      setValue('legalTermsDocumentId', (_legalTerm$id = legalTerm === null || legalTerm === void 0 ? void 0 : legalTerm.id) !== null && _legalTerm$id !== void 0 ? _legalTerm$id : '', {
                        shouldValidate: true,
                        shouldDirty: true
                      });
                    }
                  });
                }
              })]
            }, idx);
          }
        }), /*#__PURE__*/_jsx(IntroductoryMessageEditor, {
          selectedTemplateFormFieldName: "selectedTemplate",
          templates: [{
            name: 'introductoryMessages.proposal',
            dropdownLabel: t.fields.introductoryMessages.newClientTemplate()
          }, {
            name: 'introductoryMessages.invitation',
            dropdownLabel: t.fields.introductoryMessages.existingClientTemplate()
          }],
          control: control,
          formState: formState
        }), /*#__PURE__*/_jsx(IntroVideoField, {
          introVideo: introVideo,
          onChange: introVideo => setValue('introVideo', introVideo, {
            shouldDirty: true
          }),
          className: styles.video
        })]
      })]
    }), /*#__PURE__*/_jsx(NavigationGuardModal, {
      when: formState.isDirty || isLegalTermsFieldDirty,
      primaryButtonTestId: "business-settings-confirm-navigation",
      secondaryButtonTestId: "business-settings-keep-editing"
    })]
  });
}